import { flatten, unflatten } from 'flat';
import { v4 as uuidv4 } from 'uuid';
import { WebsocketProvider } from 'y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import * as Y from '../../shared/yjs';
import { WEBSOCKET_URL, getWsAuthParams } from '../../api/config';
import { buildIDBKey } from '../idb/idbKeyManager';
import { idbManifest } from '../idb/idbManifest';
import { registerOpenIDB, unregisterOpenIDB } from '../idb/idbUtils';
import { shutdownCoordinator } from '../idb/shutdownCoordinator';
import { wsTokenRefresher } from '../wsTokenRefresher';

import { createFileLogger } from '../../utils/logger';
import { docEventLog } from './docEventLog';

const log = createFileLogger('CRDTSERVICEWS_SERVICE', { enabled: true });

export interface CRDTUpdate<T> {
  type: string;
  data: T;
  timestamp: number;
}

// Special known CRDT keys and their preferred debounce times
const CRDT_KEY_CONFIGS: Record<string, number> = {
  'default': 500,           // Default debounce time (ms)
  'cursor': 0,              // Cursor data: dispatch immediately — trailing debounce causes cursors to freeze during movement
  'presence': 0,            // Presence data: dispatch immediately for real-time cursor positions
  'window': 80,             // Window movements: debounce rapid drag events
  'window-position': 80,    // Window position updates
  'window-positions': 80,   // Window position updates (new format)
  'position': 80,           // Position data: matches window debounce
};

// Private instance variable
let _instance: CRDTServiceWS | null = null;

export class CRDTServiceWS {
  private docs = new Map<string, Y.Doc>();
  private providers = new Map<string, WebsocketProvider>();
  private connections: Map<string, (update: any) => void> = new Map();
  private observers: Map<string, { observer: any; cleanup: () => void }> = new Map();
  private states = new Map<string, Y.Map<any>>();
  private localOriginId = uuidv4();
  // IndexedDB persistence for space: rooms (offline-capable window layout)
  private indexedDbs = new Map<string, IndexeddbPersistence>();
  // Per-key callbacks registered by higher-level services (e.g. docsCRDTService).
  // When a Y.Doc emits a decoding error, the registered handler is called to
  // handle full teardown + store notification. This keeps crdtServiceWS passive —
  // it detects the error but the domain layer decides what to do about it.
  private errorHandlers = new Map<string, (error: Error) => void>();

  public registerErrorHandler(key: string, handler: (error: Error) => void): void {
    this.errorHandlers.set(key, handler);
  }

  public unregisterErrorHandler(key: string): void {
    this.errorHandlers.delete(key);
  }

  constructor() {
    shutdownCoordinator.register({
      name: 'crdtServiceWS:destroy',
      phase: 'destroy',
      handler: () => {
        try { this.cleanup(); } catch {}
      }
    });
  }

  public getYDoc(key: string): Y.Doc | undefined {
    return this.docs.get(key);
  }

  /**
   * Ensures the Y.Doc for `key` is initialized (creates it if needed, attaches IDB persistence)
   * and waits for IndexedDB to finish loading its persisted state before resolving.
   * For rooms without IDB persistence this resolves immediately.
   */
  public waitForIdbSynced(key: string): Promise<void> {
    this.ensureInitialized(key);
    const idb = this.indexedDbs.get(key);
    if (!idb) return Promise.resolve();
    return (idb as any).whenSynced.then(() => undefined);
  }

  public ensureInitialized(key: string, options?: { connectImmediately?: boolean }): Y.Doc {
    let doc = this.docs.get(key);
    let needsProvider = false;
    
    if (!doc) {
      doc = new Y.Doc();
      this.docs.set(key, doc);
      needsProvider = true;
      docEventLog.log(key, 'ydoc-created', `clientID=${doc.clientID}`);
      log.log(`[CRDT-WS] Created new Y.Doc for key ${key}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
    } else {
      needsProvider = !this.providers.has(key);
      log.log(`[CRDT-WS] Reusing existing Y.Doc for key ${key}. Provider exists:${!needsProvider}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
    }

    // Create provider if needed (either new doc or existing doc without provider)
    if (needsProvider) {
      const roomId = key;
      const connectImmediately = options?.connectImmediately ?? true;

      // ── IndexedDB persistence for space:, space-chat:, doc:, note:, terminal: rooms ──
      // Adds the offline layer so layout, per-user tab state, and document content
      // survive reload, tab close, and crashes. The WebSocket provider starts
      // DISCONNECTED and only connects after IndexedDB has loaded local state —
      // without this the provider broadcasts an empty Y.Doc to the server before IDB
      // has synced, which can overwrite persisted content.
      // space-chat:{spaceId}:{userId} is per-user so IDB keys are automatically
      // isolated (the full room name, including userId, is used as the IDB key).
      const needsIdb = !this.indexedDbs.has(roomId) && (
        roomId.startsWith('space:') || roomId.startsWith('space-chat:') ||
        roomId.startsWith('doc:') || roomId.startsWith('note:') || roomId.startsWith('terminal:')
      );
      if (needsIdb) {
        const idbType = (roomId.startsWith('space:') || roomId.startsWith('space-chat:'))
          ? 'space-layout' as const
          : 'room-crdt' as const;
        const idbKey = buildIDBKey(idbType, roomId);
        const idb = new IndexeddbPersistence(idbKey, doc);
        this.indexedDbs.set(roomId, idb);
        registerOpenIDB(idbKey);

        const entityId = roomId.includes(':') ? roomId.split(':')[1] : roomId;
        idbManifest.register(idbKey, idbType, entityId);
      }

      // Rooms with IDB must start disconnected; all other rooms use the
      // caller-supplied connectImmediately option.
      const shouldConnectImmediately = needsIdb ? false : connectImmediately;
      
      try {
        const provider = new WebsocketProvider(WEBSOCKET_URL, roomId, doc, {
          connect: shouldConnectImmediately,
          params: getWsAuthParams(),
          resyncInterval: 30000,
          // Default maxBackoffTime is 2500ms — too aggressive during worker cycling.
          // With rapid successive drops the backoff counter resets on every successful open,
          // keeping retries at 100ms intervals and hammering the backend. 30s cap prevents this.
          maxBackoffTime: 30000,
        });

        // Connect only after IndexedDB has loaded local content to avoid
        // broadcasting empty state to the server before IDB has synced.
        if (needsIdb) {
          const idb = this.indexedDbs.get(roomId)!;
          // Must match the idbType used at registration above.
          const idbKey = buildIDBKey(
            (roomId.startsWith('space:') || roomId.startsWith('space-chat:')) ? 'space-layout' : 'room-crdt',
            roomId
          );
          let preWsSyncSize = 0;
          idb.once('synced', () => {
            log.log(`[CRDT] 📦 IndexedDB synced for ${roomId.slice(-8)}, connecting WebSocket`);
            idbManifest.touchLastAccessed(idbKey);
            const t0 = performance.now();
            preWsSyncSize = Y.encodeStateAsUpdateV2(doc).byteLength;
            console.log(
              `[CRDT staleCheck] idb.synced encodeV2 ${roomId.slice(0, 28)}: ` +
              `${(performance.now() - t0).toFixed(1)}ms, size=${preWsSyncSize}b`,
            );
            docEventLog.log(key, 'idb-synced', `stateBytes=${preWsSyncSize} → connecting WS`);
            provider.connect();
          });

          // After WS sync, check if the merged doc grew significantly beyond
          // what IDB had. Large growth means IDB was very stale (e.g. user was
          // offline while others rewrote the document). In that case, clear and
          // re-seed IDB so it doesn't accumulate divergent tombstone history.
          let staleCheckDone = false;
          provider.once('sync', () => {
            if (staleCheckDone) return;
            staleCheckDone = true;
            try {
              const t0 = performance.now();
              const postSyncSize = Y.encodeStateAsUpdateV2(doc).byteLength;
              console.log(
                `[CRDT staleCheck] ws.sync encodeV2 ${roomId.slice(0, 28)}: ` +
                `${(performance.now() - t0).toFixed(1)}ms, ` +
                `${preWsSyncSize}b → ${postSyncSize}b`,
              );
              const growth = postSyncSize - preWsSyncSize;
              const growthRatio = preWsSyncSize > 0 ? postSyncSize / preWsSyncSize : 1;
              if (growthRatio > 3 && growth > 50000) {
                log.warn(`[CRDT] IDB stale for ${roomId.slice(-8)}: ${preWsSyncSize}b → ${postSyncSize}b (${growthRatio.toFixed(1)}x). Clearing IDB to reseed.`);
                docEventLog.log(key, 'idb-stale-cleared', `${preWsSyncSize}b → ${postSyncSize}b (${growthRatio.toFixed(1)}x growth), clearing IDB`);
                void (idb as any).clearData();
              } else {
                docEventLog.log(key, 'ws-first-sync', `${preWsSyncSize}b → ${postSyncSize}b`);
              }
            } catch (err) {
              log.error(`[CRDT] Stale IDB check failed for ${roomId.slice(-8)}:`, err);
            }
          });
        }
        
        // Single unified status handler
        provider.on('status', ({ status }: { status: string }) => {
          if (roomId.startsWith('doc:')) {
            const yxml = doc.getXmlFragment('prosemirror');
            log.log(`[CRDT-WS] ${roomId.slice(-8)} ${status} elements=${yxml.length}`);
          }
          if (status === 'connected') {
            if (provider.ws) provider.ws.binaryType = 'arraybuffer';
            log.log(`[CRDT-WS] Connected to ${roomId}`);
            docEventLog.log(key, 'ws-connected', `yxml=${doc.getXmlFragment('prosemirror').length} elements`);
          } else if (status === 'disconnected') {
            log.warn(`[CRDT-WS] Disconnected from ${roomId}, will auto-reconnect`);
            docEventLog.log(key, 'ws-disconnected', '');
          }
        });

        // Set binaryType immediately on creation (before first status event)
        if (provider.ws) provider.ws.binaryType = 'arraybuffer';
        
        provider.on('connection-error', (event: Event) => {
          log.error(`[CRDT-WS] Connection error for ${roomId}:`, event);
          // The provider will automatically retry connections
        });
        
        provider.on('connection-close', (event: CloseEvent | null) => {
          if (event) {
            log.warn(`[CRDT-WS] Connection closed for ${roomId}:`, event.code, event.reason);
          } else {
            log.warn(`[CRDT-WS] Connection closed for ${roomId}`);
          }
        });
        
        // Y.Doc error handler: stop receiving corrupt frames, then delegate full
        // teardown to the registered domain-layer handler (e.g. docsCRDTService).
        // crdtServiceWS only disconnects — it does NOT evict from its own cache so
        // the handler can call this.disconnect(key) as part of a coordinated cleanup.
        // @ts-expect-error - Y.Doc does support 'error' event but TypeScript types are incomplete
        doc.on('error', (error: Error) => {
          log.error(`[CRDT-WS] 🚨 Y.Doc internal error for ${roomId}:`, error.message);
          docEventLog.log(key, 'ydoc-error', error.message);

          const handler = this.errorHandlers.get(key);
          if (handler) {
            try { provider.disconnect(); } catch {}
            docEventLog.log(key, 'corruption-handler-called', 'delegating to domain layer');
            handler(error);
          } else {
            // No domain handler registered (e.g. non-doc: rooms used directly).
            // Fall back to autonomous eviction — same behaviour as before.
            const isDecodingError = error.message?.includes('Unexpected end of array') ||
                                    error.message?.includes('decoding') ||
                                    error.message?.includes('decoder') ||
                                    error.message?.includes('corrupt');
            if (isDecodingError) {
              log.warn(`[CRDT-WS] No error handler registered for ${roomId}, evicting from cache.`);
              try { provider.disconnect(); } catch {}
              this.docs.delete(key);
              this.providers.delete(key);
            }
          }
        });
        
        this.providers.set(key, provider);
        wsTokenRefresher.register(provider);
        log.log(`[CRDT-WS] Created provider for ${roomId}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
      } catch (error) {
        log.error(`[CRDT-WS] Failed to create WebSocket provider for ${roomId}:`, error);
        // Continue without WebSocket - the doc will still work locally
      }
    }
    
    log.log(`[CRDT-WS] ensureInitialized complete for ${key}:`, {
      hasDoc: !!doc,
      docClientId: doc.clientID,
      hasProvider: this.providers.has(key),
      totalDocs: this.docs.size,
      totalProviders: this.providers.size
    });
    
    return doc;
  }

  // Function to get the appropriate debounce time based on key
  private getDebounceTime(key: string): number {
    // Check if the key contains any of our special types
    for (const keyType of Object.keys(CRDT_KEY_CONFIGS)) {
      if (keyType !== 'default' && key.includes(keyType)) {
        return CRDT_KEY_CONFIGS[keyType];
      }
    }
    // Fall back to default
    return CRDT_KEY_CONFIGS.default;
  }

  private debouncedObserver(key: string, ymap: Y.Map<any>, onUpdate: (update: any) => void) {
    // Create a debounced version of the observer that waits a short period before firing
    let timeoutId: any = null;
    let lastUpdate: any = null;
    let cancelled = false;

    // Determine the debounce time based on the key type
    const debounceTime = this.getDebounceTime(key);

    const observer = (event: Y.YMapEvent<any>, transaction: Y.Transaction) => {
      if (cancelled) return;
      if (transaction.origin === this.localOriginId) {
        return;
      }

      const data = this.ymapToObject(ymap);
      if (!data) {
        return;
      }

      lastUpdate = {
        type: 'update',
        data: data,
        timestamp: Date.now(),
      };

      // For zero-debounce items, send immediately
      if (debounceTime === 0) {
        try {
          onUpdate(lastUpdate);
        } catch (error) {
          log.error(`[CRDT-WS] Error in immediate update callback for key: ${key}:`, error);
        }
        return;
      }

      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set a new timeout with the appropriate debounce time
      timeoutId = setTimeout(() => {
        timeoutId = null;
        // The observer may have been cancelled while the timer was pending
        // (cleanup races against the timer). Without this check, a stale
        // callback fires AFTER unobserve and writes wrong-space state — the
        // root cause of cross-space window contamination during space
        // switches. Reference: see docs/Kernel/Storage & Sync/YJS_ARCHITECTURE_CANONICAL.md.
        if (cancelled) return;
        try {
          onUpdate(lastUpdate);
        } catch (error) {
          log.error(`[CRDT-WS] Error in debounced update callback for key: ${key}:`, error);
        }
      }, debounceTime);
    };

    // Expose a way for the connect() caller to cancel both the live observer
    // and any pending debounced fire.
    (observer as any).__cancel = () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastUpdate = null;
    };

    return observer;
  }

  connect<T>(key: string, onUpdate: (update: CRDTUpdate<T>) => void): () => void {
    const doc = this.ensureInitialized(key);

    const ymap = doc.getMap<T>(key);
    this.states.set(key, ymap);

    const debouncedObserver = this.debouncedObserver(key, ymap, onUpdate);

    ymap.observe(debouncedObserver);

    this.connections.set(key, onUpdate);
    this.observers.set(key, {
      observer: debouncedObserver,
      cleanup: () => {
        ymap.unobserve(debouncedObserver);
        // Cancel any pending debounced fire so it doesn't run after the
        // caller has torn down. See debouncedObserver above.
        const cancel = (debouncedObserver as any).__cancel as undefined | (() => void);
        if (typeof cancel === 'function') cancel();
        this.connections.delete(key);
        this.observers.delete(key);
      },
    });

    return () => {
      const observer = this.observers.get(key);
      if (observer) {
        observer.cleanup();
      }
    };
  }

  update<T extends object>(
    key: string,
    newData: T,
    options: {
      preserveOtherConnections?: boolean;
      preserveNoteConnections?: boolean;
      updateType?: 'update' | 'remove';
    } = {},
  ) {
    try {
      // Use the cached doc directly when available to avoid the full ensureInitialized
      // overhead on hot paths like presence cursor updates (called 10×/sec).
      const doc = this.docs.get(key) ?? this.ensureInitialized(key);
      const ymap = doc.getMap(key);
      const updateMap = this.objectToYmap<T>(newData);

      doc.transact(() => {
        if (options.updateType === 'remove') {
          this.removeMapEntries(ymap, updateMap);
        } else if (options.updateType === 'update') {
          this.updateMap(ymap, updateMap);
        }
      }, this.localOriginId);

      this.states.set(key, ymap);

      if (!options.preserveOtherConnections) {
        this.cleanupObservers(key, options);
      }
    } catch (error) {
      log.error(`[CRDT-WS] Error in update method for key: ${key}:`, error);
      throw error;
    }
  }

  disconnect(key: string) {
    const provider = this.providers.get(key);
    if (provider) {
      try {
        // Clear local awareness state before closing
        try { (provider as any).awareness?.setLocalState(null); } catch {}
        // Prefer destroy() for a hard close to avoid lingering server peers
        if (typeof (provider as any).destroy === 'function') {
          (provider as any).destroy();
        } else {
          provider.disconnect();
        }
      } catch {}
      wsTokenRefresher.unregister(provider);
      this.providers.delete(key);
    }
    const idb = this.indexedDbs.get(key);
    if (idb) {
      try { idb.destroy(); } catch {}
      this.indexedDbs.delete(key);
      const idbType = (key.startsWith('space:') || key.startsWith('space-chat:'))
        ? 'space-layout' as const
        : 'room-crdt' as const;
      const idbKey = buildIDBKey(idbType, key);
      unregisterOpenIDB(idbKey);
      idbManifest.unregister(idbKey);
    }
    this.docs.delete(key);
    this.errorHandlers.delete(key);
    docEventLog.log(key, 'ws-evicted', `docs=${this.docs.size} remaining`);
    log.log(`[CRDT-WS] Disconnected and removed key ${key}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
  }

  cleanup() {
    // Copy keys to avoid iterator mutation issues
    const keys = Array.from(this.providers.keys());
    for (const key of keys) {
      this.disconnect(key); // disconnect() handles idb.destroy() + idbManifest.unregister()
    }
  }

  public hasIdbForKey(key: string): boolean {
    return this.indexedDbs.has(key);
  }

  // DEBUG HELPERS

  getProvider(key: string): WebsocketProvider | undefined {
    return this.providers.get(key);
  }

  connectProvider(key: string): void {
    const provider = this.providers.get(key);
    if (provider && !provider.wsconnected) {
      provider.connect();
      log.log(`[CRDT-WS] Manually connected provider for ${key}`);
    }
  }

  getActiveDocs(): Map<string, Y.Doc> {
    return this.docs;
  }

  getActiveProviders(): Map<string, WebsocketProvider> {
    return this.providers;
  }

  // Emergency cleanup: remove all docs/providers for a specific doc id
  cleanupDocId(docId: string) {
    const key = `doc:${docId}`;
    try {
      this.disconnect(key);
      log.log(`[CRDT-WS] cleanupDocId: force disconnected ${key}`);
    } catch (e) {
      log.error('[CRDT-WS] cleanupDocId error:', e);
    }
  }

  get(key: string) {
    return this.docs.get(key);
  }

  private updateMap(ymap: Y.Map<any>, updateMap: Map<string, any>) {
    for (const [k, v] of updateMap.entries()) {
      try {
        if (ymap.get(k) !== v) {
          ymap.set(k, v);
        }
      } catch (keyError) {
        log.error('[CRDT-WS] map: Error setting key:', k, 'Error:', keyError);
        // Continue with other keys even if one fails
      }
    }
  }

  private removeMapEntries(ymap: Y.Map<any>, updateMap: Map<string, any>) {
    for (const [k, v] of updateMap.entries()) {
      // Special handling for null values - explicitly delete the key
      if (v === null || v === undefined) {
        if (ymap.has(k)) {
          ymap.delete(k);
        }
        continue;
      }
      
      // For other patterns, look for matching keys or patterns
      ymap.forEach((value, mapKey) => {
        if (mapKey === k || mapKey.startsWith(k + '.')) {
          ymap.delete(mapKey);
        }
      });
    }
  }

  private cleanupObservers(key: string, options: { preserveNoteConnections?: boolean }) {
    for (const [connKey, observer] of this.observers.entries()) {
      if (connKey === key || (!options.preserveNoteConnections && !connKey.startsWith('note-'))) {
        observer.cleanup();
      }
    }
  }

  //
  // CRDT YMAP HELPERS
  //
  private ymapToObject<T>(ymap: Y.Map<T>): T | null {
    try {
      const ymapObject = ymap.toJSON();
      if (!ymapObject || typeof ymapObject !== 'object' || Object.keys(ymapObject).length === 0) {
        return null;
      }

      // Deep convert nested Y.js objects to plain JS — toJSON() only converts
      // the top-level Y.Map; nested Y.Maps need explicit recursive conversion.
      const deepConvert = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj !== 'object') return obj;
        if (obj.toJSON && typeof obj.toJSON === 'function') {
          return deepConvert(obj.toJSON());
        }
        if (Array.isArray(obj)) {
          return obj.map(deepConvert);
        }
        const converted: any = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            converted[key] = deepConvert(obj[key]);
          }
        }
        return converted;
      };

      const fullyConverted = deepConvert(ymapObject);
      const normalizedObject = this.removeParentEntries(fullyConverted);
      return unflatten(normalizedObject) as T;
    } catch (error) {
      log.error(`[CRDT-WS] Error in ymapToObject:`, error);
      return null;
    }
  }

  private objectToYmap<T>(object: T) {
    try {
      const flatObject = flatten(object);
      const result = new Map(Object.entries(flatObject as Record<string, any>));
      return result;
    } catch (error) {
      log.error(`[CRDT-WS] Error in objectToYmap:`, error);
      throw error;
    }
  }

  private removeParentEntries(object: any) {
    const keys = Object.keys(object);
    const parentKeys = keys.filter((key) => {
      return keys.some((k) => k.startsWith(key + '.'));
    });
    parentKeys.forEach((key) => {
      delete object[key];
    });
    return object;
  }

  /** Returns a snapshot of all active Y.Doc rooms for the debug panel. */
  public getDebugSnapshot(): Array<{
    room: string;
    wsConnected: boolean;
    wsSynced: boolean;
    wsUrl: string;
    docKeys: string[];
  }> {
    const result: ReturnType<typeof this.getDebugSnapshot> = [];
    for (const [room, doc] of this.docs.entries()) {
      const provider = this.providers.get(room);
      const docKeys: string[] = [];
      doc.share.forEach((_val, key) => docKeys.push(key));
      result.push({
        room,
        wsConnected: provider?.wsconnected ?? false,
        wsSynced: provider?.synced ?? false,
        wsUrl: (provider as any)?.url ?? '',
        docKeys,
      });
    }
    return result;
  }
}

// Export a function to get the singleton instance
export function getCRDTServiceInstance(): CRDTServiceWS {
  if (!_instance) {
    _instance = new CRDTServiceWS();
  }
  return _instance;
}

// Add the missing export that's being imported elsewhere
export const crdtServiceWS = getCRDTServiceInstance();
